/**
 * Work List Excel(xlsx) 내보내기 미리보기 — 세로 A4 비율 프레임. 시트 영역은 세로·가로 스크롤 없음(`overflow: hidden`).
 * 로고·제목·갤러리·페이지 번호 없음. `WorkListWorkbookExcel` 열 비율·정합.
 */

import React from 'react';
import styled from 'styled-components';
import type { WorkListPdfTableRow } from './DocumentWorkListHorizontal';
import {
  WORK_LIST_XLSX_EXPORT_FOOTNOTE_EN,
  getWorkListXlsxColumnWidthWeights,
} from './WorkListWorkbookExcel';

export interface WorkListExcelPreviewVerticalProps {
  tableRows: WorkListPdfTableRow[];
  includeArtist: boolean;
  includePrice: boolean;
  includeNote: boolean;
  singleLineCaption?: boolean;
  captionHeaderLabel?: string;
  loading?: boolean;
}

/** thead(라벨 1행) + tbody(데이터 n행 + 푸터 1행) 합이 13을 넘으면 접기: 앞 9행 + … + 마지막 2작품 + 푸터 */
const PREVIEW_MAX_VISIBLE_ROWS_INCL_LABEL_AND_FOOTER = 13;
const PREVIEW_HEAD_ROWS = 9;
const PREVIEW_TAIL_ROWS = 2;

type PreviewBodyItem =
  | { type: 'row'; rowIndex: number }
  | { type: 'ellipsis' };

const buildPreviewBodyItems = (rows: readonly WorkListPdfTableRow[]): PreviewBodyItem[] => {
  const n = rows.length;
  const totalRowsIfFull = 1 + n + 1;
  if (totalRowsIfFull <= PREVIEW_MAX_VISIBLE_ROWS_INCL_LABEL_AND_FOOTER) {
    return rows.map((_, i) => ({ type: 'row', rowIndex: i }));
  }
  const out: PreviewBodyItem[] = [];
  for (let i = 0; i < PREVIEW_HEAD_ROWS; i++) {
    out.push({ type: 'row', rowIndex: i });
  }
  out.push({ type: 'ellipsis' });
  for (let j = n - PREVIEW_TAIL_ROWS; j < n; j++) {
    out.push({ type: 'row', rowIndex: j });
  }
  return out;
};

const WorkListExcelPreviewVertical: React.FC<WorkListExcelPreviewVerticalProps> = ({
  tableRows,
  includeArtist,
  includePrice,
  includeNote,
  singleLineCaption = false,
  captionHeaderLabel = 'Caption',
  loading = false,
}) => {
  const colCount =
    6 + (includeArtist ? 1 : 0) + (includePrice ? 1 : 0) + (includeNote ? 1 : 0)
      + (singleLineCaption ? 1 : 0);
  const columnWeights = getWorkListXlsxColumnWidthWeights(
    includeArtist,
    includePrice,
    includeNote,
    singleLineCaption,
  );
  const columnWeightSum = columnWeights.reduce((a, b) => a + b, 0);
  const previewBodyItems = buildPreviewBodyItems(tableRows);

  const renderDataRow = (row: WorkListPdfTableRow, rowIndex: number) => (
    <tr key={`xlsx-preview-${rowIndex}-${row.thumbSrc || rowIndex}`}>
      <TdCenter>{row.displayNo ?? rowIndex + 1}</TdCenter>
      <TdImage>
        {row.thumbSrc ? (
          <ThumbImg src={row.thumbSrc} alt="" loading="lazy" />
        ) : (
          <CellDash>-</CellDash>
        )}
      </TdImage>
      {includeArtist && (
        <TdLeftShell>
          <PlaceholderCellInner $align="left">
            <PreviewGrayBlock aria-hidden $kind="pill" />
          </PlaceholderCellInner>
        </TdLeftShell>
      )}
      <TdLeftShell>
        <PlaceholderCellInner $align="left">
          <PreviewGrayBlock aria-hidden $kind="title" />
        </PlaceholderCellInner>
      </TdLeftShell>
      <TdCenterShell>
        <PlaceholderCellInner $align="center">
          <PreviewGrayBlock aria-hidden $kind="short" />
        </PlaceholderCellInner>
      </TdCenterShell>
      <TdCenterShell>
        <PlaceholderCellInner $align="center">
          <PreviewGrayBlock aria-hidden $kind="pill" />
        </PlaceholderCellInner>
      </TdCenterShell>
      <TdCenterShell>
        <PlaceholderCellInner $align="center">
          <PreviewGrayBlock aria-hidden $kind="short" />
        </PlaceholderCellInner>
      </TdCenterShell>
      {includePrice && (
        <TdCenterShell>
          <PlaceholderCellInner $align="center">
            <PreviewGrayBlock aria-hidden $kind="pill" />
          </PlaceholderCellInner>
        </TdCenterShell>
      )}
      {includeNote && (
        <TdLeftShell>
          <PlaceholderCellInner $align="left">
            <PreviewGrayBlock aria-hidden $kind="pill" />
          </PlaceholderCellInner>
        </TdLeftShell>
      )}
      {singleLineCaption && (
        <TdLeftShell>
          <PlaceholderCellInner $align="left">
            <PreviewGrayBlock aria-hidden $kind="pill" />
          </PlaceholderCellInner>
        </TdLeftShell>
      )}
    </tr>
  );

  return (
    <ExcelPreviewSlot>
      <A4ExcelPortrait>
        <SheetBody>
          {loading && (
            <LoadingOverlay aria-busy="true">
              <LoadingLabel>…</LoadingLabel>
            </LoadingOverlay>
          )}
          <TableNoScrollWrap>
            <StyledTable>
              <colgroup>
                {columnWeights.map((w, index) => (
                  <col
                    key={`xlsx-col-${index}`}
                    style={{
                      width: columnWeightSum > 0 ? `${(100 * w) / columnWeightSum}%` : undefined,
                    }}
                  />
                ))}
              </colgroup>
              <thead>
                <tr>
                  <Th>No.</Th>
                  <Th>Image</Th>
                  {includeArtist && <Th>Artist</Th>}
                  <Th>Title</Th>
                  <Th>Year</Th>
                  <Th>Material</Th>
                  <Th>Size</Th>
                  {includePrice && <Th>Price</Th>}
                  {includeNote && <Th>Note</Th>}
                  {singleLineCaption && <Th>{captionHeaderLabel}</Th>}
                </tr>
              </thead>
              <tbody>
                {previewBodyItems.map((item) =>
                  item.type === 'ellipsis' ? (
                    <tr key="xlsx-preview-ellipsis">
                      <EllipsisCell colSpan={colCount}>...</EllipsisCell>
                    </tr>
                  ) : (
                    renderDataRow(
                      tableRows[item.rowIndex],
                      item.rowIndex,
                    )
                  ),
                )}
                <tr>
                  <FooterNote colSpan={colCount}>{WORK_LIST_XLSX_EXPORT_FOOTNOTE_EN}</FooterNote>
                </tr>
              </tbody>
            </StyledTable>
          </TableNoScrollWrap>
        </SheetBody>
      </A4ExcelPortrait>
    </ExcelPreviewSlot>
  );
};

/** `WorkListA4PreviewVertical`과 동일 — 슬롯 안에서 A4 세로 비율(210:297) 유지 */
const ExcelPreviewSlot = styled.div`
  width: 100%;
  height: 70%;
  min-height: 0;
  max-height: 55vh;
  container-type: size;
  container-name: work-list-excel-a4-slot;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const A4ExcelPortrait = styled.div`
  position: relative;
  box-sizing: border-box;
  aspect-ratio: 210 / 297;
  width: min(100cqw, calc(100cqh * 210 / 297));
  max-width: 100%;
  max-height: 100%;
  flex-shrink: 0;
  border: 1px solid ${({ theme }) => theme.colors.color_Gray_05};
  background-color: ${({ theme }) => theme.colors.color_Alpha_04};
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const SheetBody = styled.div`
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 1vh;
  box-sizing: border-box;
`;

const TableNoScrollWrap = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  min-width: 0;
  width: 100%;
  overflow: hidden;
`;

const StyledTable = styled.table`
  width: 100%;
  max-width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  ${({ theme }) => theme.typography.Footer_02};
  color: ${({ theme }) => theme.colors.color_Gray_02};
  font-variant-numeric: tabular-nums;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.55);
  z-index: 1;
  pointer-events: none;
`;

const LoadingLabel = styled.span`
  ${({ theme }) => theme.typography.Footer_01};
  color: ${({ theme }) => theme.colors.color_Gray_03};
`;

const CellBorder = styled.td`
  border: 1px solid ${({ theme }) => theme.colors.color_Gray_05};
  padding: 2px 5px;
  vertical-align: middle;
  overflow: hidden;
  word-break: break-word;
`;

const Th = styled.th`
  border: 1px solid ${({ theme }) => theme.colors.color_Gray_05};
  padding: 3px 5px;
  font-weight: 700;
  text-align: center;
  vertical-align: middle;
  background: transparent;
  ${({ theme }) => theme.typography.Footer_01};
  color: ${({ theme }) => theme.colors.color_Gray_03};
`;

const TdCenter = styled(CellBorder)`
  text-align: center;
`;

const TdLeftShell = styled(CellBorder)`
  text-align: left;
`;

const TdCenterShell = styled(CellBorder)`
  text-align: center;
`;

/** `td`에는 `display:flex` 금지 — 테이블 열 레이아웃·colgroup 폭이 깨짐(Chrome 등). 안쪽에서만 플렉스 정렬 */
const PlaceholderCellInner = styled.div<{ $align: 'left' | 'center' }>`
  display: flex;
  align-items: center;
  justify-content: ${({ $align }) => ($align === 'center' ? 'center' : 'flex-start')};
  width: 100%;
  min-height: ${({ $align }) => ($align === 'center' ? '16px' : '14px')};
`;

/** `WorkListA4PreviewVertical` ThumbShell·TitleShell 등과 같은 역할의 회색 박스 */
const PreviewGrayBlock = styled.div<{ $kind: 'title' | 'pill' | 'short' }>`
  background: ${({ theme }) => theme.colors.color_Gray_05};
  opacity: 0.3;
  border-radius: 1px;
  flex-shrink: 0;
  height: ${({ $kind }) => ($kind === 'title' ? '15px' : '11px')};
  width: ${({ $kind }) =>
    $kind === 'short' ? '42px' : $kind === 'pill' ? 'min(92%, 120px)' : '100%'};
  max-width: 100%;
  box-sizing: border-box;
`;

const TdImage = styled(TdCenter)`
  height: 40px;
  padding: 1px;
`;

const ThumbImg = styled.img`
  width: auto;
  height: 34px;
  max-width: 100%;
  object-fit: contain;
  display: block;
  margin: 0 auto;
`;

const CellDash = styled.span`
  color: ${({ theme }) => theme.colors.color_Gray_03};
  opacity: 0.72;
`;

const FooterNote = styled(CellBorder)`
  text-align: right;
  font-style: italic;
  font-size: 10px;
  line-height: 1.25;
  color: #b3b3b3;
  padding-top: 4px;
  padding-bottom: 4px;
`;

/** 전 열 병합(colSpan), 가운데 `...` */
const EllipsisCell = styled(CellBorder)`
  text-align: center;
  vertical-align: middle;
  letter-spacing: 0.12em;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Footer_02};
  padding-top: 3px;
  padding-bottom: 3px;
`;

export default WorkListExcelPreviewVertical;
